// components/PrivacyPolicy.jsx
import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function PrivacyPolicy() {
    return (
        <section className="py-16 md:py-24 bg-white">
            <Helmet>
                <title>Privacy Policy | Arxis Technologies Limited</title>
                <meta name="description" content="Our committment to protecting your privacy and handling your data responsibly." />
            </Helmet>

            <div className="max-w-4xl mx-auto px-6 lg:px-8 prose prose-blue max-w-none">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-center">
                    Privacy Policy
                </h1>
                <p className="text-gray-600 mb-12 text-center">
                    Last updated: January 26, 2026
                </p>

                <p className="text-gray-700 leading-relaxed mb-10">
                    Arxis Technologies Limited ("Arxis", "we", "us", or "our") is committed to protecting your privacy and handling your personal data responsibly.
                    This Privacy Policy explains how we collect, use, disclose, store, protect, and process personal information when you use our Arxis Accounting platform
                    (the "Service"), visit our website (arxisaccounting.com or related domains), or interact with us in any way.
                </p>

                <p className="text-gray-700 leading-relaxed mb-10">
                    We operate in Nigeria and comply with the <strong>Nigeria Data Protection Act 2023 (NDPA)</strong>, the Nigeria Data Protection Regulation (NDPR),
                    and other applicable data protection laws. Where we process data of individuals outside Nigeria, we aim to align with relevant international standards
                    (including principles similar to GDPR where appropriate).
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">1. Information We Collect</h2>
                <p className="text-gray-700 mb-4">We collect the following types of information:</p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                    <li><strong>Account & Registration Data</strong>: Name, business name, email address, phone number, password, and other details you provide when signing up or updating your profile.</li>
                    <li><strong>Financial & Business Data</strong>: Invoices, receipts, supplier/customer details, transaction records, payroll information, VAT/withholding tax data, bank account references (not full card numbers), and other financial records you upload or enter into the Service.</li>
                    <li><strong>Payment Information</strong>: Billing details processed securely via third-party payment providers (we do not store full card numbers).</li>
                    <li><strong>Usage & Technical Data</strong>: IP address, browser type, device information, operating system, pages visited, time spent, referral sources, and logs (collected via cookies, analytics tools, etc.).</li>
                    <li><strong>Support & Communication Data</strong>: Messages, emails, chat transcripts, or support tickets you send us.</li>
                    <li><strong>Marketing & Preference Data</strong>: Consent choices, marketing opt-ins, and interaction history with our communications.</li>
                </ul>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">2. How We Collect Information</h2>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                    <li>Directly from you (sign-up, uploads, forms, support interactions)</li>
                    <li>Automatically through your use of the Service (cookies, server logs, analytics)</li>
                    <li>From third parties (payment processors, identity verification services — only with your consent or as necessary)</li>
                </ul>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">3. How We Use Your Information</h2>
                <p className="text-gray-700 mb-4">We use your data to:</p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                    <li>Provide, maintain, and improve the Service (including invoicing, VAT filing, payroll, dashboards)</li>
                    <li>Process transactions and send receipts/invoices</li>
                    <li>Authenticate you and secure your account</li>
                    <li>Comply with legal obligations (e.g., NDPA, tax laws, CBN regulations for fintech)</li>
                    <li>Communicate with you (support, updates, service announcements)</li>
                    <li>Prevent fraud, abuse, and unauthorized access</li>
                    <li>Send marketing communications (only with your consent)</li>
                    <li>Analyze usage to improve features and performance</li>
                </ul>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">4. Legal Basis for Processing</h2>
                <p className="text-gray-700 mb-4">We process personal data based on:</p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                    <li>Your consent (e.g., marketing emails, certain cookies)</li>
                    <li>Performance of a contract (providing the Service to you)</li>
                    <li>Legal obligations (tax compliance, regulatory reporting)</li>
                    <li>Legitimate interests (security, fraud prevention, service improvement — balanced against your rights)</li>
                </ul>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">5. Sharing & Disclosure of Information</h2>
                <p className="text-gray-700 mb-4">We do not sell your personal data. We may share it with:</p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                    <li>Service providers (cloud hosting, analytics, payment processors, email services) under strict confidentiality</li>
                    <li>Regulatory authorities (e.g., FIRS for VAT submissions you authorize, NITDA/NDPC if required)</li>
                    <li>Professional advisors (lawyers, auditors) when necessary</li>
                    <li>In the event of merger, acquisition, or asset sale</li>
                </ul>
                <p className="text-gray-700">International transfers may occur (e.g., to cloud providers in the EU/US); we use safeguards like Standard Contractual Clauses or adequacy decisions where required.</p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">6. Data Security</h2>
                <p className="text-gray-700 mb-4">
                    We implement reasonable technical and organizational measures (encryption in transit/rest, access controls, regular audits)
                    to protect your data. However, no system is 100% secure — we cannot guarantee absolute security.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">7. Data Retention</h2>
                <p className="text-gray-700">
                    We keep your data only as long as necessary for the purposes above, to comply with legal obligations, or resolve disputes.
                    Financial records may be retained longer to meet tax/audit requirements (typically 7 years in Nigeria). After that, data is anonymized or securely deleted.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">8. Your Rights</h2>
                <p className="text-gray-700 mb-4">Under NDPA/NDPR you have the right to:</p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                    <li>Access, correct, or delete your personal data</li>
                    <li>Object to or restrict processing</li>
                    <li>Withdraw consent (where consent is the basis)</li>
                    <li>Data portability (in structured format)</li>
                    <li>Lodge a complaint with the Nigeria Data Protection Commission (NDPC)</li>
                </ul>
                <p className="text-gray-700">To exercise these rights, contact us at privacy@arxisaccounting.com. We respond within 30 days (or as required by law).</p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">9. Cookies & Tracking</h2>
                <p className="text-gray-700">
                    We use essential cookies for functionality and analytics cookies (with consent) to understand usage.
                    You can manage preferences via our cookie banner or browser settings. See our separate Cookie Policy for details.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">10. Children's Privacy</h2>
                <p className="text-gray-700">
                    Our Service is not directed at children under 16. We do not knowingly collect data from children.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">11. Changes to This Policy</h2>
                <p className="text-gray-700">
                    We may update this policy from time to time. Changes will be posted here with an updated "Last updated" date.
                    Significant changes may be notified via email or in-app notice.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">12. Contact Us</h2>
                <p className="text-gray-700 mb-4">
                    For questions, rights requests, or complaints about this Privacy Policy:
                </p>
                <address className="not-italic text-gray-700">
                    Arxis Technologies Limited<br />
                    Email: arxistechnologies@gmail.com<br />
                    {/* Data Protection Officer: [Name or "DPO" if appointed]<br /> */}
                    Lagos, Nigeria
                </address>

                <p className="text-gray-600 mt-16 text-sm text-center">
                    © {new Date().getFullYear()} Arxis Technologies Limited. All rights reserved.
                </p>
            </div>
        </section>
    );
}